import {
  CheckCircleFillIcon,
  CheckCircleIcon,
  CheckIcon,
  CircleIcon,
  DotFillIcon,
  type Icon,
  QuestionIcon,
  XCircleFillIcon,
  XCircleIcon,
  XIcon,
} from '@primer/octicons-react'
import {AnchoredOverlay, Button, type ButtonProps, IconButton, Stack, Text} from '@primer/react'
import {useRef, useState} from 'react'
import {clsx} from 'clsx'

import type {CombinedStatusResult} from './index'
import {CheckStatusDialog} from './CheckStatusDialog'

import styles from './ChecksStatusBadge.module.css'

interface ChecksStatusBadgeProps {
  statusRollup: string
  combinedStatus?: CombinedStatusResult
  variant?: 'circled' | 'filled' | 'default'
  onWillOpenPopup?: () => void
  onDialogOpenChange?: (isOpen: boolean) => void
  disablePopover?: boolean
  buttonClassName?: ButtonProps['className']
  size?: ButtonProps['size']
  descriptionText?: string
  repositoryNwo?: string
}

interface IconProps {
  icon: Icon
  iconColor: string
}

const ICON_STYLE = {
  success: {
    circled: CheckCircleIcon,
    filled: CheckCircleFillIcon,
    default: CheckIcon,
    color: 'var(--bgColor-success-emphasis, var(--color-success-emphasis))',
  },
  pending: {
    circled: CircleIcon,
    filled: DotFillIcon,
    default: DotFillIcon,
    color: 'var(--bgColor-attention-emphasis, var(--color-scale-yellow-4))',
  },
  failure: {
    circled: XCircleIcon,
    filled: XCircleFillIcon,
    default: XIcon,
    color: 'var(--bgColor-danger-emphasis, var(--color-scale-red-4))',
  },
  error: {
    circled: QuestionIcon,
    filled: QuestionIcon,
    default: QuestionIcon,
    color: 'var(--fgColor-muted, var(--color-canvas-subtle))',
  },
}

interface IconOnlyStatusProps extends IconProps {
  className?: string
  descriptionText?: string
}

function IconOnlyStatus({className, descriptionText, icon: IconComponent, iconColor}: IconOnlyStatusProps) {
  return (
    <span className={clsx(className, 'd-flex flex-items-center gap-1')} data-testid="checks-status-badge-icon-only">
      <span style={{color: iconColor, display: 'inline-flex'}}>
        <IconComponent aria-label={descriptionText ? descriptionText : 'See all checks'} />
      </span>
      {descriptionText && <span> {descriptionText}</span>}
    </span>
  )
}

function ChecksStatusError({icon: IconComponent, iconColor, size}: IconProps & Pick<ChecksStatusBadgeProps, 'size'>) {
  const [isOpen, setIsOpen] = useState(false)
  const errorText = 'There was an error retrieving checks status'

  return (
    <span className="d-flex flex-items-center gap-1" data-testid="checks-status-badge-icon-only">
      <AnchoredOverlay
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        side="outside-top"
        align="center"
        width="small"
        overlayProps={{role: 'dialog', 'aria-label': 'Checks status error'}}
        renderAnchor={props => (
          <IconButton
            {...props}
            aria-haspopup={undefined}
            aria-labelledby={undefined}
            aria-label="Checks unavailable"
            icon={IconComponent}
            size={size}
            style={{color: iconColor}}
            variant="invisible"
          />
        )}
      >
        <Stack padding="normal">
          <Text>{errorText}</Text>
        </Stack>
      </AnchoredOverlay>
      <span> ?/?</span>
    </span>
  )
}

export function ChecksStatusBadge(props: ChecksStatusBadgeProps) {
  const {
    statusRollup,
    combinedStatus,
    variant = 'default',
    disablePopover,
    buttonClassName,
    size = 'medium',
    descriptionText = '',
    onWillOpenPopup: fetchData,
    onDialogOpenChange,
    repositoryNwo,
  } = props
  const [isOpen, setIsOpen] = useState(false)
  const checkButtonRef = useRef<HTMLButtonElement>(null)
  const iconStyle = ICON_STYLE[statusRollup as keyof typeof ICON_STYLE]
  const {icon, iconColor} = {
    icon: iconStyle?.[variant] || ICON_STYLE.error[variant],
    iconColor: iconStyle?.color || ICON_STYLE.error.color,
  }

  if (statusRollup === 'error') {
    return <ChecksStatusError icon={icon} iconColor={iconColor} size={size} />
  }

  if (disablePopover) {
    return <IconOnlyStatus descriptionText={descriptionText} icon={icon} iconColor={iconColor} />
  }
  return (
    <>
      {descriptionText ? (
        <Button
          data-testid="checks-status-badge-button"
          leadingVisual={icon}
          variant="invisible"
          size={size}
          aria-label={combinedStatus?.checksStatusSummary ?? `Status checks: ${statusRollup}`}
          style={{'--checks-icon-color': iconColor}}
          ref={checkButtonRef}
          onClick={() => {
            fetchData?.()
            setIsOpen(true)
            onDialogOpenChange?.(true)
          }}
          className={clsx(styles.ChecksStatusBadgeButton, buttonClassName)}
        >
          {descriptionText}
        </Button>
      ) : (
        <IconButton
          data-testid="checks-status-badge-icon"
          tooltipDirection="s"
          icon={icon}
          variant="invisible"
          size={size}
          aria-label={combinedStatus?.checksStatusSummary ?? statusRollup}
          style={{'--checks-icon-color': iconColor}}
          className={clsx(styles.ChecksStatusBadgeIconButton, buttonClassName)}
          ref={checkButtonRef}
          onClick={() => {
            fetchData?.()
            setIsOpen(true)
            onDialogOpenChange?.(true)
          }}
        />
      )}
      {isOpen && (
        <CheckStatusDialog
          combinedStatus={combinedStatus}
          isOpen={isOpen}
          onDismiss={() => {
            setIsOpen(false)
            onDialogOpenChange?.(false)
            setTimeout(() => {
              checkButtonRef.current?.focus()
            }, 0)
          }}
          repositoryNwo={repositoryNwo}
          onWorkflowsApproved={() => fetchData?.()}
        />
      )}
    </>
  )
}
